import dayjs from "dayjs";
import { z } from "zod";

import { dateString, optionalText, requiredText } from "@/lib/forms/schema";

import {
  REMINDER_COLORS,
  type CreateForeignNationalReminderRequest,
  type ForeignNationalReminder,
  type ReminderColor,
  type ReminderKind
} from "@/api/types/foreignNationalReminder";
import {
  dateInputToIso,
  normalizeText
} from "@/features/clientProfile/lib/format";

// Record helpers --------------------------------------------------------------

/** A reminder as the calendar reads it: a firm entry belongs to no client. */
export type CalendarReminder = Omit<
  ForeignNationalReminder,
  "foreign_national_id"
> & {
  foreign_national_id?: string | null;
};

/** Legacy records omit `kind`; treat anything but APPOINTMENT as a reminder. */
export const reminderKind = (r: CalendarReminder): ReminderKind =>
  r.kind === "APPOINTMENT" ? "APPOINTMENT" : "REMINDER";

export const isAllDay = (r: CalendarReminder): boolean => r.all_day === true;

export const isReminderColor = (value: unknown): value is ReminderColor =>
  typeof value === "string" &&
  (REMINDER_COLORS as readonly string[]).includes(value);

/** Stored color, else a per-kind default (appointments sky, reminders amber). */
export const reminderColor = (r: CalendarReminder): ReminderColor => {
  if (isReminderColor(r.color)) return r.color;
  return reminderKind(r) === "APPOINTMENT" ? "sky" : "amber";
};

export const KIND_LABEL: Record<ReminderKind, string> = {
  REMINDER: "Reminder",
  APPOINTMENT: "Appointment"
};

// Filters ---------------------------------------------------------------------

export type StatusFilter = "ALL" | "OPEN" | "CLOSED";
export type KindFilter = "ALL" | ReminderKind;

export type ReminderFilters = {
  search: string;
  status: StatusFilter;
  kind: KindFilter;
  assignedTo: string; // "" = anyone
};

export const DEFAULT_REMINDER_FILTERS: ReminderFilters = {
  search: "",
  status: "ALL",
  kind: "ALL",
  assignedTo: ""
};

export const STATUS_FILTER_LABEL: Record<StatusFilter, string> = {
  ALL: "All statuses",
  OPEN: "Open",
  CLOSED: "Completed & dismissed"
};

export const KIND_FILTER_LABEL: Record<KindFilter, string> = {
  ALL: "All types",
  REMINDER: "Reminders",
  APPOINTMENT: "Appointments"
};

export const activeFilterCount = (f: ReminderFilters): number =>
  [
    Boolean(f.search.trim()),
    f.status !== "ALL",
    f.kind !== "ALL",
    Boolean(f.assignedTo)
  ].filter(Boolean).length;

export const filterReminders = <T extends CalendarReminder>(
  reminders: T[],
  f: ReminderFilters
): T[] => {
  const q = f.search.trim().toLowerCase();
  return reminders.filter((r) => {
    if (f.status === "OPEN" && r.status !== "OPEN") return false;
    if (f.status === "CLOSED" && r.status === "OPEN") return false;
    if (f.kind !== "ALL" && reminderKind(r) !== f.kind) return false;
    if (f.assignedTo && !(r.assigned_to_user_ids ?? []).includes(f.assignedTo))
      return false;
    if (!q) return true;
    return (
      (r.title ?? "").toLowerCase().includes(q) ||
      (r.description ?? "").toLowerCase().includes(q) ||
      (r.location ?? "").toLowerCase().includes(q)
    );
  });
};

// Labels ----------------------------------------------------------------------

/** "3 days overdue", "Due today", "Due in 5 days", or the closed status. */
export const urgencyLabel = (
  r: CalendarReminder,
  now: dayjs.Dayjs = dayjs()
): string => {
  if (r.status !== "OPEN")
    return r.status === "DISMISSED" ? "Dismissed" : "Completed";
  const due = dayjs(r.due_date);
  if (!due.isValid()) return "";
  if (due.isBefore(now, "day")) {
    const days = now.startOf("day").diff(due.startOf("day"), "day");
    return days === 1 ? "1 day overdue" : `${days} days overdue`;
  }
  if (due.isSame(now, "day")) return "Due today";
  const days = due.startOf("day").diff(now.startOf("day"), "day");
  return days === 1 ? "Due in 1 day" : `Due in ${days} days`;
};

export const formatDueLabel = (iso?: string | null): string => {
  if (!iso) return "—";
  const d = dayjs(iso);
  return d.isValid() ? d.format("MMM D, YYYY · h:mm A") : "—";
};

// Date plumbing ---------------------------------------------------------------

/**
 * Calendar day of an all-day instant. All-day values are stored at UTC noon
 * (see `dateInputToIso`), so read the day back from the UTC components rather
 * than the viewer's zone.
 */
export const utcDayKey = (iso?: string | null): string => {
  const ms = Date.parse(iso ?? "");
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${d.getUTCFullYear()}-${mm}-${dd}`;
};

/** Local `YYYY-MM-DD` of a timed instant. */
export const localDayKey = (value: Date | string): string => {
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

/** Local `HH:mm` of a timed instant. */
export const localTimeKey = (value: Date | string): string => {
  const d = dayjs(value);
  return d.isValid() ? d.format("HH:mm") : "";
};

/** Local date + time inputs -> ISO instant; null when either part is invalid. */
export const combineLocalDateTime = (
  date: string,
  time: string
): string | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date.trim())) return null;
  if (!/^\d{2}:\d{2}$/.test(time.trim())) return null;
  const d = new Date(`${date.trim()}T${time.trim()}:00`);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
};

/** Add whole minutes to an `HH:mm` value, spilling into the next day if needed. */
export const addMinutesToTime = (
  date: string,
  time: string,
  minutes: number
): { date: string; time: string } => {
  const base = dayjs(`${date}T${time}:00`);
  if (!base.isValid()) return { date, time };
  const next = base.add(minutes, "minute");
  return { date: next.format("YYYY-MM-DD"), time: next.format("HH:mm") };
};

export type TimeOption = { value: string; label: string };

/** 15-minute steps across the day: `{ value: "09:15", label: "9:15 AM" }`. */
export const TIME_OPTIONS: TimeOption[] = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  const label = dayjs(`2000-01-01T${value}:00`).format("h:mm A");
  return { value, label };
});

/** Snap an `HH:mm` value to the nearest 15-minute option. */
export const snapToQuarterHour = (time: string): string => {
  const m = /^(\d{2}):(\d{2})$/.exec(time);
  if (!m) return "09:00";
  const total = Number(m[1]) * 60 + Number(m[2]);
  const snapped = Math.min(Math.round(total / 15) * 15, 23 * 60 + 45);
  return `${String(Math.floor(snapped / 60)).padStart(2, "0")}:${String(
    snapped % 60
  ).padStart(2, "0")}`;
};

export const DEFAULT_APPOINTMENT_MINUTES = 60;

// Editor draft ----------------------------------------------------------------

export type ReminderDraft = {
  kind: ReminderKind;
  title: string;
  description: string;
  /** `YYYY-MM-DD` */
  startDate: string;
  /** `HH:mm`, ignored when `allDay`. */
  startTime: string;
  /** Appointments only. */
  endDate: string;
  endTime: string;
  allDay: boolean;
  location: string;
  sharedWithClient: boolean;
  color: ReminderColor | null;
  assignedToUserIds: string[];
};

export type ReminderPayload = CreateForeignNationalReminderRequest;

export const emptyReminderDraft = (): ReminderDraft => ({
  kind: "REMINDER",
  title: "",
  description: "",
  startDate: "",
  startTime: "09:00",
  endDate: "",
  endTime: "10:00",
  allDay: false,
  location: "",
  sharedWithClient: false,
  color: null,
  assignedToUserIds: []
});

/** Draft seeded from a clicked calendar slot (month cell or hour row). */
export const draftForSlot = (
  start: Date,
  allDay: boolean,
  defaults: Partial<Pick<ReminderDraft, "assignedToUserIds" | "kind">> = {}
): ReminderDraft => {
  const date = localDayKey(start);
  const time = allDay ? "09:00" : snapToQuarterHour(localTimeKey(start));
  const end = addMinutesToTime(date, time, DEFAULT_APPOINTMENT_MINUTES);
  return {
    ...emptyReminderDraft(),
    kind: defaults.kind ?? "REMINDER",
    startDate: date,
    startTime: time,
    endDate: end.date,
    endTime: end.time,
    allDay,
    assignedToUserIds: defaults.assignedToUserIds ?? []
  };
};

export const reminderToDraft = (r: CalendarReminder): ReminderDraft => {
  const kind = reminderKind(r);
  const allDay = isAllDay(r);
  const startDate = allDay ? utcDayKey(r.due_date) : localDayKey(r.due_date);
  const startTime = allDay ? "09:00" : localTimeKey(r.due_date) || "09:00";
  const hasEnd = Boolean(r.end_date);
  const endDate = hasEnd
    ? allDay
      ? utcDayKey(r.end_date)
      : localDayKey(r.end_date ?? "")
    : startDate;
  const endTime = hasEnd && !allDay ? localTimeKey(r.end_date ?? "") : "";
  const fallbackEnd = addMinutesToTime(
    startDate,
    startTime,
    DEFAULT_APPOINTMENT_MINUTES
  );
  return {
    kind,
    title: normalizeText(r.title),
    description: normalizeText(r.description),
    startDate,
    startTime,
    endDate: endDate || fallbackEnd.date,
    endTime: endTime || fallbackEnd.time,
    allDay,
    location: normalizeText(r.location),
    sharedWithClient: Boolean(r.shared_with_client),
    color: isReminderColor(r.color) ? r.color : null,
    assignedToUserIds: (r.assigned_to_user_ids ?? [])
      .map((id) => id.trim())
      .filter(Boolean)
  };
};

const isAppointment = (d: ReminderDraft) => d.kind === "APPOINTMENT";

/** Start instant for a draft, or null when incomplete. */
export const draftStartIso = (d: ReminderDraft): string | null =>
  d.allDay
    ? dateInputToIso(d.startDate)
    : combineLocalDateTime(d.startDate, d.startTime);

/** End instant for a draft (appointments only), or null. */
export const draftEndIso = (d: ReminderDraft): string | null => {
  if (!isAppointment(d)) return null;
  return d.allDay
    ? dateInputToIso(d.endDate || d.startDate)
    : combineLocalDateTime(d.endDate || d.startDate, d.endTime);
};

export const reminderDraftsEqual = (
  a: ReminderDraft,
  b: ReminderDraft
): boolean => {
  const same = (x: string, y: string) => normalizeText(x) === normalizeText(y);
  if (a.kind !== b.kind || a.allDay !== b.allDay) return false;
  if (!same(a.title, b.title) || !same(a.description, b.description))
    return false;
  if (!same(a.startDate, b.startDate)) return false;
  if (!a.allDay && !same(a.startTime, b.startTime)) return false;
  if (isAppointment(a)) {
    if (!same(a.endDate, b.endDate)) return false;
    if (!a.allDay && !same(a.endTime, b.endTime)) return false;
    if (!same(a.location, b.location)) return false;
    if (a.sharedWithClient !== b.sharedWithClient) return false;
  }
  if ((a.color ?? null) !== (b.color ?? null)) return false;
  return (
    [...a.assignedToUserIds].sort().join(",") ===
    [...b.assignedToUserIds].sort().join(",")
  );
};

/**
 * Title and a start; appointments also need an end that isn't before the
 * start. Issues land on the field the user has to fix.
 */
export const reminderDraftSchema: z.ZodType<ReminderDraft, ReminderDraft> = z
  .object({
    kind: z.enum(["REMINDER", "APPOINTMENT"]) as z.ZodType<
      ReminderKind,
      ReminderKind
    >,
    title: requiredText("Title"),
    description: optionalText(),
    startDate: dateString(),
    startTime: optionalText(),
    endDate: dateString(),
    endTime: optionalText(),
    allDay: z.boolean(),
    location: optionalText(),
    sharedWithClient: z.boolean(),
    color: z
      .enum(REMINDER_COLORS as unknown as [ReminderColor, ...ReminderColor[]])
      .nullable() as z.ZodType<ReminderColor | null, ReminderColor | null>,
    assignedToUserIds: z.array(z.string())
  })
  .superRefine((d, ctx) => {
    const start = draftStartIso(d);
    if (!start) {
      if (!normalizeText(d.startDate)) {
        ctx.addIssue({
          code: "custom",
          message: d.allDay ? "Date is required." : "Start date is required.",
          path: ["startDate"]
        });
      } else if (!d.allDay) {
        ctx.addIssue({
          code: "custom",
          message: "Start time is required.",
          path: ["startTime"]
        });
      }
      return;
    }
    if (!isAppointment(d)) return;
    const end = draftEndIso(d);
    if (!end) {
      ctx.addIssue({
        code: "custom",
        message: d.allDay ? "End date is required." : "End time is required.",
        path: [d.allDay || normalizeText(d.endDate) ? "endDate" : "endTime"]
      });
      return;
    }
    if (Date.parse(end) < Date.parse(start)) {
      ctx.addIssue({
        code: "custom",
        message: "End must be on or after the start.",
        path: [d.allDay ? "endDate" : "endTime"]
      });
    }
  });

export const reminderDraftToPayload = (d: ReminderDraft): ReminderPayload => ({
  kind: d.kind,
  title: normalizeText(d.title),
  description: normalizeText(d.description) || null,
  due_date: draftStartIso(d) ?? "",
  end_date: draftEndIso(d),
  all_day: d.allDay,
  location: isAppointment(d) ? normalizeText(d.location) || null : null,
  shared_with_client: isAppointment(d) && d.sharedWithClient,
  color: d.color,
  assigned_to_user_ids: d.assignedToUserIds
});

// Sorting / DOM ---------------------------------------------------------------

const dueMs = (r: CalendarReminder): number => {
  const ms = Date.parse(r.due_date ?? "");
  return Number.isFinite(ms) ? ms : 0;
};

export const sortRemindersByDue = (
  a: CalendarReminder,
  b: CalendarReminder
): number => dueMs(a) - dueMs(b);

export const reminderFocusDomId = (id: string): string => `reminder-${id}`;
